import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Skeleton,
  Divider,
  Collapse,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Calendar,
  Building2,
  MapPin,
  Hash,
  FileText,
  ChevronDown,
  ChevronUp,
  Users,
  Globe,
  Briefcase,
  DollarSign,
  BookOpen,
  Scale,
  Landmark,
  Tag,
  Clock,
  Layers,
  AlertTriangle,
  FileDigit,
} from 'lucide-react'
import { useEffect } from 'react'
import { fetchAnnonceDetail } from '../../redux/actions/entreprisesAction'
import { useSearchTokens } from '../SearchScreen/searchDesignTokens'

const TYPE_COLORS = {
  constitution: '#22c55e',
  dissolution: '#ef4444',
  dissolution_anticipee: '#dc2626',
  modification_du_capital: '#f59e0b',
  cession_de_parts: '#8b5cf6',
  changement_de_gerant: '#3b82f6',
  transfert_de_siege: '#06b6d4',
  fusion: '#ec4899',
  transformation: '#14b8a6',
  prorogation: '#64748b',
  modification_statuts: '#f97316',
  liquidation: '#ef4444',
}

const AnnonceDetailScreen = () => {
  const { annonceId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t, i18n } = useTranslation()
  const dk = useSearchTokens()

  const annonce = useSelector((state) => state.entreprises.annonceDetail)
  const isLoading = useSelector((state) => state.entreprises.loading.detail)
  const error = useSelector((state) => state.entreprises.errors.detail)

  const [showRawText, setShowRawText] = React.useState(false)

  useEffect(() => {
    if (annonceId) {
      dispatch(fetchAnnonceDetail(annonceId))
    }
  }, [annonceId, dispatch])

  const lang = i18n.language?.split('-')[0]

  const getLocalizedText = (textObj) => {
    if (!textObj) return null
    if (typeof textObj === 'string') return textObj
    return textObj[lang] || textObj.en || textObj.fr || textObj.ar || null
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return null
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const formatCapital = (capital, devise) => {
    if (!capital) return null
    return `${Number(capital).toLocaleString('fr-FR')} ${devise || 'MAD'}`
  }

  const handleCompanyClick = () => {
    if (annonce?.registre_commerce) {
      const params = new URLSearchParams()
      if (annonce.ville_rc) params.set('ville_rc', annonce.ville_rc)
      navigate(
        `/legal-notice/company/${encodeURIComponent(annonce.registre_commerce)}${
          params.toString() ? `?${params.toString()}` : ''
        }`
      )
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ height: '100vh', backgroundColor: dk.bg, overflow: 'auto' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
          <Skeleton variant="rounded" width={120} height={32} sx={{ bgcolor: dk.chipBg, mb: 3 }} />
          <Skeleton variant="rounded" width="60%" height={28} sx={{ bgcolor: dk.chipBg, mb: 2 }} />
          <Skeleton variant="rounded" width="40%" height={20} sx={{ bgcolor: dk.chipBg, mb: 4 }} />
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" width="100%" height={60} sx={{ bgcolor: dk.chipBg, mb: 2, borderRadius: '10px' }} />
          ))}
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ height: '100vh', backgroundColor: dk.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: dk.error, mb: 2 }}>{error}</Typography>
          <IconButton onClick={() => navigate(-1)} sx={{ color: dk.gold }}>
            <ArrowLeft size={20} />
          </IconButton>
        </Box>
      </Box>
    )
  }

  if (!annonce) return null

  const typeColor = TYPE_COLORS[annonce.type_annonce] || dk.gold
  const descriptionType = getLocalizedText(annonce.description_type)
  const classification = annonce.classification_activite || {}
  const keywords = classification.mots_cles_activite || []
  const secteur = classification.secteur_principal || annonce.secteur_principal
  const dissolution = annonce.dissolution
  const depotLegal = annonce.depot_legal

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: dk.bg,
        overflow: 'auto',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: dk.scrollThumb, borderRadius: 2 },
      }}
    >
      <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 4 } }}>
        {/* Back button */}
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            color: dk.text2,
            mb: 2,
            p: 0.8,
            borderRadius: '8px',
            backgroundColor: dk.chipBg,
            '&:hover': { backgroundColor: dk.goldSub, color: dk.gold },
          }}
        >
          <ArrowLeft size={18} />
        </IconButton>

        {/* Header */}
        <Box data-tour="annonce-detail-header" sx={{ mb: 4 }}>
          {/* Type chips */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={t(`type_${annonce.type_annonce}`, annonce.type_annonce)}
              sx={{
                height: 26,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: typeColor,
                backgroundColor: `${typeColor}12`,
                border: `1px solid ${typeColor}35`,
              }}
            />
            {(annonce.types_secondaires || []).map((ts) => (
              <Chip
                key={ts}
                label={t(`type_${ts}`, ts)}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.68rem',
                  fontWeight: 500,
                  color: TYPE_COLORS[ts] || dk.text2,
                  backgroundColor: TYPE_COLORS[ts] ? `${TYPE_COLORS[ts]}12` : dk.chipBg,
                  border: `1px solid ${TYPE_COLORS[ts] ? `${TYPE_COLORS[ts]}30` : dk.border}`,
                }}
              />
            ))}
            {annonce.forme_juridique && (
              <Chip
                label={t(`forme_${annonce.forme_juridique}`, annonce.forme_juridique)}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  color: dk.gold,
                  backgroundColor: dk.goldSub,
                  border: `1px solid ${dk.goldBdr}`,
                }}
              />
            )}
            {annonce.statut_societe && (
              <Chip
                label={t(`statut_${annonce.statut_societe}`, annonce.statut_societe)}
                size="small"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500, color: dk.text2, backgroundColor: dk.chipBg }}
              />
            )}
          </Box>

          {/* Company name */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: dk.text1,
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              mb: descriptionType ? 0.5 : 1,
            }}
          >
            {annonce.denomination || t('unnamed_company', 'Société non nommée')}
          </Typography>

          {/* Description type */}
          {descriptionType && (
            <Typography sx={{ fontSize: '0.82rem', color: dk.text3, mb: 1, fontStyle: 'italic' }}>
              {descriptionType}
            </Typography>
          )}

          {/* Meta row */}
          <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
            {annonce.date_publication && (
              <MetaItem icon={<Calendar size={13} />} text={formatDate(annonce.date_publication)} dk={dk} />
            )}
            {annonce.registre_commerce && (
              <MetaItem icon={<Hash size={13} />} text={`RC: ${annonce.registre_commerce}`} dk={dk} />
            )}
            {annonce.ville && (
              <MetaItem icon={<MapPin size={13} />} text={annonce.ville} dk={dk} />
            )}
            {annonce.ice && (
              <MetaItem icon={<FileText size={13} />} text={`ICE: ${annonce.ice}`} dk={dk} />
            )}
            {annonce.numero_gazette && (
              <MetaItem icon={<BookOpen size={13} />} text={`BO n°${annonce.numero_gazette}`} dk={dk} />
            )}
          </Box>
        </Box>

        {/* Sections */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Company link */}
          {annonce.registre_commerce && (
            <InfoCard dk={dk}>
              <Box
                data-tour="annonce-view-company"
                onClick={handleCompanyClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  py: 0.5,
                  '&:hover': { '& .link-text': { textDecoration: 'underline' } },
                }}
              >
                <Building2 size={18} color={dk.gold} />
                <Box>
                  <Typography className="link-text" sx={{ fontSize: '0.85rem', fontWeight: 600, color: dk.gold }}>
                    {t('view_company', 'Voir la fiche entreprise')}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: dk.text3 }}>
                    {annonce.denomination} — RC {annonce.registre_commerce}
                  </Typography>
                </Box>
              </Box>
            </InfoCard>
          )}

          {/* Key figures — capital, dates, duration, shares */}
          {(() => {
            const items = [
              annonce.capital_social && {
                icon: <DollarSign size={14} />,
                label: t('capital', 'Capital'),
                value: formatCapital(annonce.capital_social, annonce.devise),
              },
              annonce.date_creation && {
                icon: <Calendar size={14} />,
                label: t('date_creation', 'Date de création'),
                value: formatDate(annonce.date_creation),
              },
              annonce.date_immatriculation && {
                icon: <Calendar size={14} />,
                label: t('date_immatriculation', 'Immatriculation'),
                value: formatDate(annonce.date_immatriculation),
              },
              annonce.duree != null && {
                icon: <Clock size={14} />,
                label: t('duree', 'Durée'),
                value: `${annonce.duree} ${t('ans', 'ans')}`,
              },
              annonce.nombre_parts_total != null && {
                icon: <Layers size={14} />,
                label: t('parts_sociales', 'Parts sociales'),
                value: annonce.valeur_nominale_part
                  ? `${Number(annonce.nombre_parts_total).toLocaleString('fr-FR')} × ${Number(annonce.valeur_nominale_part).toLocaleString('fr-FR')} MAD`
                  : Number(annonce.nombre_parts_total).toLocaleString('fr-FR'),
              },
            ].filter(Boolean)

            if (!items.length) return null
            return (
              <InfoCard dk={dk}>
                <Box data-tour="annonce-detail-figures" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {items.map(({ icon, label, value }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ color: dk.text3, display: 'flex', mt: '3px', flexShrink: 0 }}>{icon}</Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                          {value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </InfoCard>
            )
          })()}

          {/* Siege Social */}
          {getLocalizedText(annonce.siege_social) && (
            <InfoCard dk={dk} icon={<MapPin size={15} />} title={t('siege_social', 'Siège social')}>
              <Typography sx={{ fontSize: '0.85rem', color: dk.text1, lineHeight: 1.6 }}>
                {getLocalizedText(annonce.siege_social)}
              </Typography>
              {annonce.localisation?.quartier && getLocalizedText(annonce.localisation.quartier) && (
                <Typography sx={{ fontSize: '0.75rem', color: dk.text3, mt: 0.5 }}>
                  {getLocalizedText(annonce.localisation.quartier)}
                </Typography>
              )}
            </InfoCard>
          )}

          {/* Objet Social */}
          {getLocalizedText(annonce.objet_social) && (
            <InfoCard dk={dk} icon={<Briefcase size={15} />} title={t('objet_social', 'Objet social')}>
              <Typography sx={{ fontSize: '0.85rem', color: dk.text1, lineHeight: 1.6 }}>
                {getLocalizedText(annonce.objet_social)}
              </Typography>
            </InfoCard>
          )}

          {/* Activity / Classification */}
          {(secteur || keywords.length > 0) && (
            <InfoCard dk={dk} icon={<Tag size={15} />} title={t('activite', 'Activité')}>
              {secteur && (
                <Box sx={{ mb: keywords.length > 0 ? 1.5 : 0 }}>
                  <Chip
                    label={t(`secteur_${secteur}`, secteur.replace(/_/g, ' '))}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: dk.gold,
                      backgroundColor: dk.goldSub,
                      border: `1px solid ${dk.goldBdr}`,
                      '& .MuiChip-label': { px: 1 },
                    }}
                  />
                </Box>
              )}
              {keywords.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                  {keywords.map((kw, idx) => (
                    <Chip
                      key={idx}
                      label={getLocalizedText(kw)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 500,
                        color: dk.text2,
                        backgroundColor: dk.elevated,
                        border: `1px solid ${dk.borderLight}`,
                        '& .MuiChip-label': { px: 0.8 },
                      }}
                    />
                  ))}
                </Box>
              )}
            </InfoCard>
          )}

          {/* Personnes */}
          {annonce.personnes && annonce.personnes.length > 0 && (
            <InfoCard dk={dk} icon={<Users size={15} />} title={t('personnes', 'Associés & Dirigeants')}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {annonce.personnes.map((person, idx) => {
                  const name = getLocalizedText(person.nom_complet) || person.nom || person.name
                  const subText = [person.nationalite, person.cin || person.passeport].filter(Boolean).join(' · ')
                  const sharesLabel = person.pourcentage_capital != null
                    ? `${person.pourcentage_capital}%`
                    : person.nombre_parts != null
                    ? `${Number(person.nombre_parts).toLocaleString('fr-FR')} parts`
                    : null

                  return (
                    <Box
                      key={idx}
                      sx={{
                        py: 1,
                        px: 1.2,
                        borderRadius: '6px',
                        backgroundColor: dk.elevated,
                        border: `1px solid ${dk.borderLight}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                          {name || '-'}
                        </Typography>
                        {(person.role || person.qualite) && (
                          <Typography sx={{ fontSize: '0.7rem', color: dk.text3, mt: 0.2 }}>
                            {person.role || person.qualite}
                          </Typography>
                        )}
                        {subText && (
                          <Typography sx={{ fontSize: '0.68rem', color: dk.text3, mt: 0.1 }}>
                            {subText}
                          </Typography>
                        )}
                      </Box>
                      {sharesLabel && (
                        <Typography
                          sx={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            color: dk.gold,
                            backgroundColor: dk.goldSub,
                            px: 0.8,
                            py: 0.2,
                            borderRadius: '4px',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {sharesLabel}
                        </Typography>
                      )}
                    </Box>
                  )
                })}
              </Box>
            </InfoCard>
          )}

          {/* Dissolution details */}
          {dissolution && (
            <InfoCard dk={dk} icon={<AlertTriangle size={15} />} title={t('dissolution_details', 'Détails dissolution')}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {getLocalizedText(dissolution.motif) && (
                  <Box>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3 }}>
                      {t('motif', 'Motif')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: dk.text1, lineHeight: 1.6 }}>
                      {getLocalizedText(dissolution.motif)}
                    </Typography>
                  </Box>
                )}
                {dissolution.date_dissolution && (
                  <Box>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3 }}>
                      {t('date_dissolution', 'Date de dissolution')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                      {formatDate(dissolution.date_dissolution)}
                    </Typography>
                  </Box>
                )}
                {getLocalizedText(dissolution.liquidateur) && (
                  <Box>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3 }}>
                      {t('liquidateur', 'Liquidateur')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                      {getLocalizedText(dissolution.liquidateur)}
                    </Typography>
                  </Box>
                )}
                {getLocalizedText(dissolution.siege_liquidation) && (
                  <Box>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3 }}>
                      {t('siege_liquidation', 'Siège de liquidation')}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: dk.text2, lineHeight: 1.5 }}>
                      {getLocalizedText(dissolution.siege_liquidation)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </InfoCard>
          )}

          {/* Depot légal */}
          {depotLegal && (getLocalizedText(depotLegal.tribunal) || depotLegal.date_depot) && (
            <InfoCard dk={dk} icon={<Landmark size={15} />} title={t('depot_legal', 'Dépôt légal')}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {getLocalizedText(depotLegal.tribunal) && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ color: dk.text3, display: 'flex', mt: '3px', flexShrink: 0 }}>
                      <Scale size={13} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('tribunal', 'Tribunal')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                        {getLocalizedText(depotLegal.tribunal)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {depotLegal.date_depot && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ color: dk.text3, display: 'flex', mt: '3px', flexShrink: 0 }}>
                      <Calendar size={13} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('date_depot', 'Date de dépôt')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                        {formatDate(depotLegal.date_depot)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                {depotLegal.numero_depot && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <Box sx={{ color: dk.text3, display: 'flex', mt: '3px', flexShrink: 0 }}>
                      <FileDigit size={13} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        {t('numero_depot', 'N° de dépôt')}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                        {depotLegal.numero_depot}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </InfoCard>
          )}

          {/* Legal identifiers */}
          {(() => {
            const ids = [
              annonce.identifiant_fiscal && { label: t('id_fiscal', 'Identifiant fiscal'), value: annonce.identifiant_fiscal },
              annonce.taxe_professionnelle && { label: t('taxe_pro', 'Taxe professionnelle'), value: annonce.taxe_professionnelle },
              annonce.cnss && { label: 'CNSS', value: annonce.cnss },
            ].filter(Boolean)
            if (!ids.length) return null
            return (
              <InfoCard dk={dk} icon={<FileDigit size={15} />} title={t('identifiants', 'Identifiants')}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {ids.map(({ label, value }) => (
                    <Box key={label}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: dk.text3, textTransform: 'uppercase', letterSpacing: '0.04em', mb: 0.3 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1, fontFamily: '"JetBrains Mono", "Fira Code", monospace' }}>
                        {value}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </InfoCard>
            )
          })()}

          {/* Cessions de parts */}
          {annonce.cessions_parts && annonce.cessions_parts.length > 0 && (
            <InfoCard dk={dk} icon={<BookOpen size={15} />} title={t('cessions_parts', 'Cessions de parts')}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {annonce.cessions_parts.map((cession, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.8,
                      px: 1,
                      borderRadius: '6px',
                      backgroundColor: dk.elevated,
                      border: `1px solid ${dk.borderLight}`,
                    }}
                  >
                    <Typography sx={{ fontSize: '0.82rem', color: dk.text1 }}>
                      {cession.cedant && <span style={{ fontWeight: 600 }}>{getLocalizedText(cession.cedant) || cession.cedant}</span>}
                      {cession.cedant && cession.cessionnaire && <span style={{ color: dk.text3 }}> → </span>}
                      {cession.cessionnaire && <span style={{ fontWeight: 600 }}>{getLocalizedText(cession.cessionnaire) || cession.cessionnaire}</span>}
                    </Typography>
                    {cession.nombre_parts != null && (
                      <Typography sx={{ fontSize: '0.72rem', color: dk.text3, mt: 0.3 }}>
                        {Number(cession.nombre_parts).toLocaleString('fr-FR')} parts
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </InfoCard>
          )}

          {/* Raw text (collapsible) */}
          {(annonce.texte_brut_fr || annonce.texte_brut_ar) && (
            <InfoCard dk={dk}>
              <Box
                onClick={() => setShowRawText(!showRawText)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Globe size={15} color={dk.text2} />
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: dk.text1 }}>
                    {t('raw_text', 'Texte original')}
                  </Typography>
                </Box>
                {showRawText ? <ChevronUp size={16} color={dk.text3} /> : <ChevronDown size={16} color={dk.text3} />}
              </Box>
              <Collapse in={showRawText}>
                <Divider sx={{ my: 1, borderColor: dk.border }} />
                {annonce.texte_brut_fr && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: dk.text3, mb: 0.5, textTransform: 'uppercase' }}>
                      Français
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: dk.text2, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                      {annonce.texte_brut_fr}
                    </Typography>
                  </Box>
                )}
                {annonce.texte_brut_ar && (
                  <Box>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: dk.text3, mb: 0.5, textTransform: 'uppercase' }}>
                      العربية
                    </Typography>
                    <Typography dir="rtl" sx={{ fontSize: '0.85rem', color: dk.text2, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                      {annonce.texte_brut_ar}
                    </Typography>
                  </Box>
                )}
              </Collapse>
            </InfoCard>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────

const MetaItem = ({ icon, text, dk }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Box sx={{ color: dk.disabled, display: 'flex' }}>{icon}</Box>
    <Typography sx={{ fontSize: '0.78rem', color: dk.text3, fontWeight: 500 }}>
      {text}
    </Typography>
  </Box>
)

const InfoCard = ({ dk, icon, title, children }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: '10px',
      border: `1px solid ${dk.border}`,
      backgroundColor: dk.surface,
    }}
  >
    {title && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ color: dk.gold, display: 'flex' }}>{icon}</Box>
        <Typography
          sx={{
            fontSize: '0.76rem',
            fontWeight: 700,
            color: dk.text2,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {title}
        </Typography>
      </Box>
    )}
    {children}
  </Box>
)

export default AnnonceDetailScreen